import classNames from 'clsx';
import throttle from 'lodash.throttle';
import React, { memo, ReactElement, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { TOP_TOOLBAR_ID } from 'reactApp/constants/magicIdentificators';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { useCustomActions } from 'reactApp/ui/Toolbar/customActions/useCustomActions';
import { ToolbarGapMode, ToolbarProps, TToolbarItem } from 'reactApp/ui/Toolbar/Toolbar.types';
import { ToolbarItem } from 'reactApp/ui/Toolbar/ToolbarItem/ToolbarItem';
import { useActions } from 'reactApp/ui/Toolbar/useActions';
import { useFilterActions } from 'reactApp/ui/Toolbar/useFilterActions';
import useResizeObserver from 'use-resize-observer/polyfilled';

import styles from './Toolbar.css';

const MAX_NUM_OF_BUTTON_POSIYION_TRIES = 10;

export const Toolbar = memo(({ gapMode, toolbarType }: ToolbarProps): ReactElement => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const leftRef = useRef<HTMLDivElement | null>(null);
    const rightRef = useRef<HTMLDivElement | null>(null);
    const counterRef = useRef(0);
    const timerRef = useRef<number | null>(null);

    const storage = useSelector(getCurrentStorage) as EStorageType;

    const filters = useFilterActions({ storage, toolbarType });
    const actions = useActions({ storage, toolbarType });
    const { left: leftCustomActions, right: rightCustomActions } = useCustomActions({ storage });

    const [priority, setPriority] = useState(0);

    const leftActions = [...leftCustomActions, ...actions];
    const rightActions = [...filters, ...rightCustomActions];

    const setResetStackCounter = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = window.setTimeout(() => {
            counterRef.current = 0;
        }, 1000);
    };

    const findButtonPriority = useCallback((): void => {
        if (leftActions.length && leftRef?.current && rootRef?.current) {
            const leftWidth = leftRef.current.getBoundingClientRect()?.width;
            const rightWidth = rightRef?.current ? rightRef?.current.getBoundingClientRect()?.width : 0;
            const rootWidth = rootRef.current.getBoundingClientRect()?.width;

            const emptyPlace = rootWidth - leftWidth - rightWidth;

            if (emptyPlace <= 10 && priority < 20) {
                if (counterRef.current > MAX_NUM_OF_BUTTON_POSIYION_TRIES) {
                    // useState для этого каунтера использовать нельзя, так как его изменение вызывает перерендер и сбрасывая каунтер, мы опять попадаем в начало этого цикла
                    setResetStackCounter();
                    return;
                }
                setPriority(priority + 1);
                counterRef.current++;
            } else if (emptyPlace > 150 && priority > 0) {
                setPriority(priority - 1);
                counterRef.current++;
            }
        }
    }, [leftActions.length, priority]);

    const renderItem = useCallback(
        ({ Item, itemProps, ...data }: TToolbarItem) => {
            if (Item) {
                return <Item parentRef={rootRef} {...itemProps} />;
            }

            return (
                <ToolbarItem
                    key={data.id}
                    id={data.id}
                    text={data.text}
                    onClick={data.onClick}
                    hint={data.hint}
                    icon={data.icon}
                    collapse={!!data?.priority && data?.priority <= priority}
                    list={data.list}
                    parentRef={rootRef}
                    dropdownTheme={data.dropdownTheme}
                    dropdownPosition={data.dropdownPosition}
                    withoutDropdownIcon={data.withoutDropdownIcon}
                    openDropdownByArrow={data.openDropdownByArrow}
                    tooltipId={data?.tooltipId}
                />
            );
        },
        [priority]
    );

    const handleOnResize = useMemo(() => throttle(findButtonPriority, 500), [findButtonPriority]);

    useResizeObserver({ onResize: handleOnResize, ref: rootRef });

    useLayoutEffect(() => {
        findButtonPriority();
    }, [findButtonPriority]);

    return (
        <div className={styles.root} ref={rootRef} id={TOP_TOOLBAR_ID} data-qa-id="toolbar">
            {Boolean(leftActions.length) && (
                <div
                    className={classNames({
                        [styles.left]: true,
                        [styles.wideGap]: gapMode === ToolbarGapMode.wide,
                    })}
                    ref={leftRef}
                >
                    {leftActions.map(renderItem)}
                </div>
            )}
            {Boolean(rightActions.length) && (
                <div
                    className={classNames({
                        [styles.right]: true,
                        [styles.wideGap]: gapMode === ToolbarGapMode.wide,
                    })}
                    ref={rightRef}
                >
                    {rightActions.map(renderItem)}
                </div>
            )}
        </div>
    );
});

Toolbar.displayName = 'Toolbar';
